<template>
  <v-dialog
    :model-value="modelValue"
    :persistent="loading"
    max-width="418"
    @after-leave="emit('close')"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <v-card
      class="dialog-card confirm-dialog"
      rounded="xl"
    >
      <div class="confirm-dialog__header">
        <div class="confirm-dialog__title-row">
          <span
            class="confirm-dialog__icon"
            :class="`confirm-dialog__icon--${variant}`"
          >
            <v-icon
              icon="mdi-alert-outline"
              size="20"
            />
          </span>

          <div class="confirm-dialog__heading">
            <h3 class="dialog-title">{{ title }}</h3>
            <p
              v-if="subtitle"
              class="dialog-subtitle"
            >
              {{ subtitle }}
            </p>
          </div>
        </div>

        <v-btn
          aria-label="Zatvori dialog"
          class="button button--icon dialog-close"
          :disabled="loading"
          icon="mdi-close"
          size="x-small"
          variant="text"
          @click="closeDialog"
        />
      </div>

      <div class="confirm-dialog__body">
        <p class="dialog-message">
          {{ message }}
        </p>
      </div>

      <div class="confirm-dialog__actions">
        <v-btn
          v-if="variant === 'confirm'"
          class="button button--dialog button--secondary confirm-dialog__button confirm-dialog__button--cancel"
          :disabled="loading"
          variant="outlined"
          @click="cancelDialog"
        >
          {{ secondaryText }}
        </v-btn>

        <v-btn
          class="button button--dialog confirm-dialog__button"
          :class="variant === 'confirm' ? 'button--danger' : 'button--dark'"
          :disabled="loading"
          :loading="loading"
          variant="flat"
          @click="confirmDialog"
        >
          {{ primaryText }}
        </v-btn>
      </div>
    </v-card>
  </v-dialog>
</template>

<script setup>
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false,
  },

  loading: {
    type: Boolean,
    default: false,
  },

  closeOnConfirm: {
    type: Boolean,
    default: true,
  },

  variant: {
    type: String,
    default: 'confirm',
    validator: (value) => ['confirm', 'blocked'].includes(value),
  },

  title: {
    type: String,
    required: true,
  },

  subtitle: {
    type: String,
    default: '',
  },

  message: {
    type: String,
    required: true,
  },

  primaryText: {
    type: String,
    default: 'Potvrdi',
  },

  secondaryText: {
    type: String,
    default: 'Odustani',
  },
})

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel', 'close'])

function closeDialog () {
  emit('update:modelValue', false)
}

function cancelDialog () {
  emit('cancel')
  closeDialog()
}

function confirmDialog () {
  emit('confirm')

  if (props.closeOnConfirm) {
    closeDialog()
  }
}
</script>

<style
  scoped
  lang="scss"
>
.confirm-dialog {
  --confirm-dialog-padding: #{clamp-bp("sm", "xl", 16, 24)};

  &__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: var(--confirm-dialog-padding);
  }

  &__title-row {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    min-width: 0;
  }

  &__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 24px;
    height: 24px;

    &--confirm {
      color: var(--color-danger);
    }

    &--blocked {
      color: var(--color-warning);
    }
  }

  &__heading {
    min-width: 0;
  }

  &__body {
    padding: var(--confirm-dialog-padding);
  }

  &__button {
    &--cancel.v-btn {
      border-color: var(--color-border-soft);
      color: var(--color-dialog-text);
    }
  }

  &__actions {
    display: flex;
    flex-direction: column-reverse;
    gap: 10px;
    justify-content: flex-end;
    padding: var(--confirm-dialog-padding);

    @include breakpoint-up("sm") {
      align-items: center;
      flex-direction: row;
    }
  }
}
</style>
